import React, { useEffect } from 'react';

interface PreloadAssetsProps {
  fonts?: string[];
  images?: string[];
  scripts?: string[];
  stylesheets?: string[];
}

/**
 * Ultra-high performance asset preloading component
 * Optimized for old PCs with efficient resource management
 * Features: DNS prefetch, preload critical resources, lazy loading non-critical assets
 */
export const PreloadAssets = React.memo(function PreloadAssets({
  fonts = [],
  images = [],
  scripts = [],
  stylesheets = [],
}: PreloadAssetsProps) {
  useEffect(() => {
    const preloadElement = (href: string, as: string, type?: string) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = href;
      link.as = as;
      if (type) link.type = type;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      return link;
    };

    const prefetchDNS = (hostname: string) => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = hostname;
      document.head.appendChild(link);
      return link;
    };

    // Preload critical fonts with proper timing
    const fontLinks = fonts.map((font) =>
      preloadElement(font, 'font', 'font/woff2'),
    );

    // Preload critical images with proper timing
    const imageLinks = images.map((image) => preloadElement(image, 'image'));

    // Preload critical scripts
    const scriptLinks = scripts.map((script) =>
      preloadElement(script, 'script'),
    );

    // Preload critical stylesheets
    const stylesheetLinks = stylesheets.map((stylesheet) =>
      preloadElement(stylesheet, 'style'),
    );

    // DNS prefetch for external domains
    const dnsLinks = [
      prefetchDNS('//fonts.googleapis.com'),
      prefetchDNS('//fonts.gstatic.com'),
    ];

    // Cleanup function
    return () => {
      [
        ...fontLinks,
        ...imageLinks,
        ...scriptLinks,
        ...stylesheetLinks,
        ...dnsLinks,
      ].forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    };
  }, [fonts, images, scripts, stylesheets]);

  return null;
});

/**
 * Critical resource preloader for MainLayout
 * Preloads essential assets for faster loading on old PCs
 * Optimized to prevent preload warnings
 */
export const MainLayoutPreloader = React.memo(function MainLayoutPreloader() {
  return (
    <PreloadAssets
      // Remove logo preload since it's loaded eagerly in TopLogo component
      // This prevents the "preloaded but not used" warning
      images={[]}
      // Remove font preloads since they're already declared in CSS with font-display: swap
      // This prevents the preload warnings
      fonts={[]}
    />
  );
});

/**
 * Performance hints component for browser optimization
 * Provides hints to the browser for better resource management
 */
export const PerformanceHints = React.memo(function PerformanceHints() {
  useEffect(() => {
    // Add performance hints to document head
    const hints = [
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
      },
      { name: 'theme-color', content: '#ffffff' },
      { name: 'color-scheme', content: 'light dark' },
      { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
    ];

    const metaTags = hints.map((hint) => {
      const meta = document.createElement('meta');
      Object.entries(hint).forEach(([key, value]) => {
        meta.setAttribute(key, value);
      });
      document.head.appendChild(meta);
      return meta;
    });

    // Resource hints for better performance
    const resourceHints = [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
    ];

    const linkTags = resourceHints.map((hint) => {
      const link = document.createElement('link');
      Object.entries(hint).forEach(([key, value]) => {
        if (key === 'crossOrigin') {
          link.crossOrigin = value as string;
        } else {
          link.setAttribute(key, value as string);
        }
      });
      document.head.appendChild(link);
      return link;
    });

    // Cleanup
    return () => {
      [...metaTags, ...linkTags].forEach((tag) => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });
    };
  }, []);

  return null;
});

/**
 * Service worker registration for caching and offline support
 * Optimized for old PCs with efficient caching strategies
 */
export const ServiceWorkerProvider = React.memo(
  function ServiceWorkerProvider() {
    useEffect(() => {
      if (
        'serviceWorker' in navigator &&
        process.env.NODE_ENV === 'production'
      ) {
        window.addEventListener('load', () => {
          navigator.serviceWorker
            .register('/sw.js')
            .then((_registration) => {
            })
            .catch((_registrationError) => {
            });
        });
      }
    }, []);

    return null;
  },
);
